/** Advanced analysis: spatial-frequency spectrum of the profile and slanted-edge MTF. */
import { computed, signal } from '@preact/signals';
import { spectrum, type Detrend, type WindowFn } from '../../core/fft';
import { slantedEdgeMtf } from '../../core/mtf';
import { computePlane } from '../../core/pipeline';
import { normalizeRect, type RectRoi } from '../../core/roi';
import { fmt, freqUnit, unitScale } from '../../core/units';
import {
  measurement,
  mtfRoiId,
  rois,
  sampling,
  selectedResult,
  selectedRoi,
  spatial,
  spectrumSettings,
  updateSignal,
} from '../../app/store';
import { download } from '../../app/exporters';
import { toCsv } from '../../core/report';
import { PlotView } from '../../plot/PlotView';
import { Button, Field, Hint, KV, Segmented, Select } from '../controls';

const tab = signal<'spectrum' | 'mtf'>('spectrum');
const mtfAxis = signal<'px' | 'unit'>('px');

const spectrumResult = computed(() => {
  const r = selectedResult.value;
  if (!r) return null;
  const dx = sampling.value.step * unitScale(spatial.value);
  return spectrum(r.profile.v, dx, spectrumSettings.value);
});

const mtfResult = computed(() => {
  const m = measurement.value;
  const id = mtfRoiId.value ?? (selectedRoi.value?.kind === 'rect' ? selectedRoi.value.id : null);
  const r = rois.value.find((x) => x.id === id && x.kind === 'rect') as RectRoi | undefined;
  if (!m || !r) return null;
  const b = normalizeRect(r);
  // MTF needs a linear quantity: always use luminance Y of the current image.
  const plane = computePlane(m.ctx, 'Y', { x: b.x, y: b.y, w: b.w, h: b.h });
  return { roi: r, res: slantedEdgeMtf(plane) };
});

function SpectrumTab() {
  const s = spectrumResult.value;
  const ss = spectrumSettings.value;
  const sp = spatial.value;
  const fu = freqUnit(sp);
  return (
    <div class="analysis-tab">
      <div class="toolbar-row">
        <Field label="Window" help="spectrum.window">
          <Select<WindowFn>
            value={ss.window}
            options={[
              { value: 'hann', label: 'Hann' },
              { value: 'hamming', label: 'Hamming' },
              { value: 'blackman', label: 'Blackman' },
              { value: 'none', label: 'Rectangular' },
            ]}
            onChange={(v) => updateSignal(spectrumSettings, { window: v })}
          />
        </Field>
        <Field label="Detrend" help="spectrum.detrend">
          <Select<Detrend>
            value={ss.detrend}
            options={[
              { value: 'linear', label: 'Linear' },
              { value: 'mean', label: 'Mean' },
              { value: 'none', label: 'None' },
            ]}
            onChange={(v) => updateSignal(spectrumSettings, { detrend: v })}
          />
        </Field>
        <Field label="Zero-pad" help="spectrum.zeropad">
          <Select<number>
            value={ss.zeroPad}
            options={[1, 2, 4, 8, 16].map((v) => ({ value: v, label: `${v}×` }))}
            onChange={(v) => updateSignal(spectrumSettings, { zeroPad: v })}
          />
        </Field>
      </div>
      {!s ? (
        <div class="muted pad">Select a line to analyse its spectrum.</div>
      ) : (
        <>
          <KV
            testId="spectrum-kv"
            rows={[
              ['Dominant frequency', s.dominant ? `${fmt(s.dominant.freq, 6)} ${fu}` : '—', 'spectrum.dominant'],
              ['Period (pitch)', s.dominant ? `${fmt(s.dominant.period, 6)} ${sp.unit}` : '—', 'spectrum.period'],
              ['Amplitude', s.dominant ? fmt(s.dominant.amp, 5) : '—', 'spectrum.amp'],
              ['Samples analysed', `${s.used}`, 'spectrum.used'],
              ['Nyquist frequency', `${fmt(0.5 / s.dx, 5)} ${fu}`, 'spectrum.nyquist'],
            ]}
          />
          <div class="plot-box">
            <PlotView
              spec={{
                series: [{ id: 'amp', label: 'Amplitude', x: s.freq, y: s.amp, color: '#6aa8ff' }],
                vlines: s.dominant
                  ? [{ x: s.dominant.freq, color: '#ffd166', dash: [4, 3], label: `${fmt(s.dominant.period, 4)} ${sp.unit}` }]
                  : [],
                xLabel: `Spatial frequency [${fu}]`,
                yLabel: `Amplitude [${measurement.value?.unit || '1'}]`,
              }}
              resetKey={`${sp.unit}`}
            />
          </div>
          <Button
            small
            icon="download"
            help="spectrum.export"
            onClick={() =>
              download(
                toCsv(
                  [[`frequency_${fu}`, 'amplitude'], ...Array.from(s.freq, (f, i) => [f, s.amp[i]])],
                  [`Aperto spectrum; window ${ss.window}; detrend ${ss.detrend}; zero-pad ${ss.zeroPad}x`],
                ),
                'aperto_spectrum.csv',
                'text/csv',
              )
            }
          >
            Spectrum CSV
          </Button>
        </>
      )}
    </div>
  );
}

function MtfTab() {
  const res = mtfResult.value;
  const rects = rois.value.filter((r) => r.kind === 'rect');
  const sp = spatial.value;
  const k = unitScale(sp);
  const useUnit = mtfAxis.value === 'unit' && sp.unit !== 'px';
  const fu = useUnit ? freqUnit(sp) : 'cyc/px';
  const conv = (f: number | null) => (f === null ? null : useUnit ? f / k : f);
  const lp = (f: number | null) => (f === null ? '—' : `${fmt(conv(f), 5)} ${fu}`);
  return (
    <div class="analysis-tab">
      <div class="toolbar-row">
        <Field label="Edge ROI" help="mtf.roi">
          <Select<string>
            value={res?.roi.id ?? mtfRoiId.value ?? ''}
            options={[
              { value: '', label: rects.length ? 'Selected rectangle' : 'Draw a rectangle (R) over an edge' },
              ...rects.map((r) => ({ value: r.id, label: r.name })),
            ]}
            onChange={(v) => (mtfRoiId.value = v || null)}
          />
        </Field>
        <Field label="Frequency axis" help="mtf.axis">
          <Segmented
            value={mtfAxis.value}
            options={[
              { value: 'px', label: 'cyc/px' },
              { value: 'unit', label: sp.unit === 'px' ? 'cyc/unit' : freqUnit(sp), disabled: sp.unit === 'px' },
            ]}
            onChange={(v) => (mtfAxis.value = v)}
          />
        </Field>
      </div>
      {!res ? (
        <Hint>Place a rectangle ROI across a slightly slanted (≈ 2–10°) straight edge. The MTF is computed from linear luminance Y.</Hint>
      ) : (
        <>
          {res.res.warnings.map((w) => (
            <Hint kind="warn" key={w}>
              {w}
            </Hint>
          ))}
          <KV
            testId="mtf-kv"
            rows={[
              ['MTF50', lp(res.res.mtf50), 'mtf.mtf50'],
              ['MTF10', lp(res.res.mtf10), 'mtf.mtf10'],
              ['MTF at Nyquist (0.5 cyc/px)', fmt(res.res.mtfAtNyquist, 4), 'mtf.nyquist'],
              ['Edge angle', `${fmt(res.res.angleDeg, 4)}° (${res.res.orientation})`, 'mtf.angle'],
              ['Rows used', `${res.res.rows}`, 'mtf.rows'],
            ]}
          />
          <div class="plot-box">
            <PlotView
              spec={{
                series: [
                  { id: 'mtf', label: 'MTF', x: useUnit ? res.res.freq.map((f) => f / k) : res.res.freq, y: res.res.mtf, color: '#6aa8ff' },
                ],
                hlines: [
                  { y: 0.5, color: '#7f8a99', label: '50 %' },
                  { y: 0.1, color: '#7f8a99', label: '10 %' },
                ],
                vlines: [{ x: useUnit ? 0.5 / k : 0.5, color: '#ff8c69', dash: [4, 3], label: 'Nyquist' }],
                xLabel: `Spatial frequency [${fu}]`,
                yLabel: 'MTF',
                yRange: [0, 1.1],
              }}
              resetKey={fu}
            />
          </div>
          <div class="plot-box small">
            <PlotView
              spec={{
                series: [
                  { id: 'esf', label: 'ESF', x: res.res.esfX, y: res.res.esf, color: '#9aa4b2' },
                  { id: 'lsf', label: 'LSF', x: res.res.esfX, y: res.res.lsf, color: '#8fd694', axis: 'y2' },
                ],
                xLabel: 'Distance from edge [px]',
                yLabel: 'ESF (Y)',
                y2Label: 'LSF',
              }}
              resetKey={res.roi.id}
            />
          </div>
          <Button
            small
            icon="download"
            help="mtf.export"
            onClick={() =>
              download(
                toCsv(
                  [
                    ['frequency_cyc_per_px', `frequency_${freqUnit(sp)}`, 'mtf'],
                    ...Array.from(res.res.freq, (f, i) => [f, f / k, res.res.mtf[i]]),
                  ],
                  [
                    `Aperto slanted-edge MTF; ROI ${res.roi.name}; edge angle ${res.res.angleDeg.toFixed(3)} deg; MTF50 ${res.res.mtf50 ?? ''} cyc/px`,
                  ],
                ),
                'aperto_mtf.csv',
                'text/csv',
              )
            }
          >
            MTF CSV
          </Button>
        </>
      )}
    </div>
  );
}

export function AnalysisPanel() {
  return (
    <div class="analysis-panel">
      <Segmented
        value={tab.value}
        testId="analysis-tab"
        help="analysis.tabs"
        options={[
          { value: 'spectrum', label: 'Spectrum / pitch' },
          { value: 'mtf', label: 'Slanted-edge MTF' },
        ]}
        onChange={(v) => (tab.value = v)}
      />
      {tab.value === 'spectrum' ? <SpectrumTab /> : <MtfTab />}
    </div>
  );
}
